export const CART_SCHEMA_VERSION = 1;
export const CART_STORAGE_KEY = "quickdrop.customer.cart.v1";

const cleanText = (value, maxLength = 500) => String(value ?? "").trim().slice(0, maxLength);
const cleanMoney = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
};
const cleanStock = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? Math.trunc(number) : 0;
};

export function cartSource(product = {}) {
  const storeId = cleanText(product.storeId || product.shopId, 160);
  return {
    sourceType: storeId ? "shop" : "catalog",
    storeId,
  };
}

export function cartItemKey(item = {}) {
  const sourceType = item.sourceType === "shop" ? "shop" : "catalog";
  const storeId = sourceType === "shop" ? cleanText(item.storeId, 160) : "global";
  return `${sourceType}:${storeId}:${cleanText(item.productId || item.id, 160)}`;
}

export function productToCartItem(product, quantity = 1) {
  const { sourceType, storeId } = cartSource(product);
  const stock = cleanStock(product.stock);
  return {
    productId: cleanText(product.id || product.productId, 160),
    quantity: Math.max(1, Math.min(stock, Math.trunc(Number(quantity) || 1))),
    sourceType,
    storeId,
    storeName: cleanText(product.shopName || product.storeName, 160),
    name: cleanText(product.name || "Product", 240),
    unitPriceSnapshot: cleanMoney(product.price),
    oldPriceSnapshot: cleanMoney(product.mrp || product.oldPrice),
    stockSnapshot: stock,
    imageUrl: cleanText(product.images?.[0] || product.imageUrl, 2000),
    weight: cleanText(product.weight, 80),
    unit: cleanText(product.unit || product.measureUnit, 80),
    availability: stock > 0 && product.isActive !== false ? "available" : "unavailable",
  };
}

export function sourceLabel(item = {}) {
  return item.sourceType === "shop" && item.storeId
    ? cleanText(item.storeName || "this local shop", 160)
    : "QuickDrop catalog";
}

export function cartSourcesConflict(items, product) {
  if (!items.length) return false;
  const target = productToCartItem(product);
  const current = items[0];
  return current.sourceType !== target.sourceType
    || (current.sourceType === "shop" && current.storeId !== target.storeId);
}

function sanitizeSavedItem(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const productId = cleanText(value.productId, 160);
  const quantity = Math.trunc(Number(value.quantity));
  if (!productId || !Number.isFinite(quantity) || quantity <= 0) return null;
  const sourceType = value.sourceType === "shop" ? "shop" : "catalog";
  const storeId = sourceType === "shop" ? cleanText(value.storeId, 160) : "";
  if (sourceType === "shop" && !storeId) return null;
  const stockSnapshot = cleanStock(value.stockSnapshot);
  return {
    productId,
    quantity: stockSnapshot > 0 ? Math.min(quantity, stockSnapshot) : quantity,
    sourceType,
    storeId,
    storeName: cleanText(value.storeName, 160),
    name: cleanText(value.name || "Saved product", 240),
    unitPriceSnapshot: cleanMoney(value.unitPriceSnapshot),
    oldPriceSnapshot: cleanMoney(value.oldPriceSnapshot),
    stockSnapshot,
    imageUrl: cleanText(value.imageUrl, 2000),
    weight: cleanText(value.weight, 80),
    unit: cleanText(value.unit, 80),
    availability: ["available", "unavailable", "removed"].includes(value.availability)
      ? value.availability
      : "available",
  };
}

export function decodeStoredCart(raw) {
  if (!raw) return { items: [], notice: "" };
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CART_SCHEMA_VERSION || !Array.isArray(parsed.items)) {
      return { items: [], notice: "Your saved cart used an unsupported format and was safely reset." };
    }
    const items = parsed.items.map(sanitizeSavedItem).filter(Boolean);
    return {
      items,
      notice: items.length === parsed.items.length
        ? ""
        : "Some invalid saved cart items were safely removed.",
    };
  } catch {
    return { items: [], notice: "Your saved cart could not be read and was safely reset." };
  }
}

export function loadStoredCart(storage = globalThis?.localStorage) {
  try {
    return decodeStoredCart(storage?.getItem(CART_STORAGE_KEY));
  } catch {
    return { items: [], notice: "Saved cart storage is unavailable. You can still shop in this tab." };
  }
}

export function saveStoredCart(items, storage = globalThis?.localStorage) {
  try {
    storage?.setItem(CART_STORAGE_KEY, JSON.stringify({
      version: CART_SCHEMA_VERSION,
      items,
      updatedAt: new Date().toISOString(),
    }));
    return true;
  } catch {
    return false;
  }
}

export function quantityForProduct(items, product) {
  const targetKey = cartItemKey(productToCartItem(product));
  return items.find((item) => cartItemKey(item) === targetKey)?.quantity || 0;
}

export function changeCartQuantity(items, product, delta) {
  const item = productToCartItem(product);
  const key = cartItemKey(item);
  const index = items.findIndex((entry) => cartItemKey(entry) === key);
  if (index < 0) {
    return delta > 0 && item.stockSnapshot > 0 ? [...items, item] : items;
  }
  const nextQuantity = Math.max(0, Math.min(item.stockSnapshot, items[index].quantity + delta));
  if (!nextQuantity) return items.filter((_, itemIndex) => itemIndex !== index);
  return items.map((entry, itemIndex) => itemIndex === index
    ? { ...item, quantity: nextQuantity }
    : entry);
}

export function reconcileCartItems(items, products, shopProducts) {
  const current = new Map(
    [...products, ...shopProducts].map((product) => [cartItemKey(productToCartItem(product)), product]),
  );
  const notices = [];
  const reconciled = items.map((item) => {
    const product = current.get(cartItemKey(item));
    if (!product) {
      notices.push(`${item.name} is no longer available and needs to be removed.`);
      return { ...item, availability: "removed", stockSnapshot: 0 };
    }
    const fresh = productToCartItem(product, item.quantity);
    if (fresh.availability !== "available") {
      notices.push(`${item.name} is currently unavailable.`);
      return { ...fresh, quantity: item.quantity, availability: "unavailable" };
    }
    if (item.quantity > fresh.stockSnapshot) {
      notices.push(`${item.name} was adjusted to ${fresh.stockSnapshot} because stock changed.`);
    }
    return { ...fresh, quantity: Math.min(item.quantity, fresh.stockSnapshot) };
  });
  return { items: reconciled, notices };
}
